import clsx from 'clsx';

import { Flex } from '@dynatrace/strato-components/layouts';

import { RangeLegendMarkers } from './components/RangeLegendMarkers/RangeLegendMarkers.js';
import { RangeLegendSegments } from './components/RangeLegendSegments/RangeLegendSegments.js';
import {
  CONTAINER_PADDING,
  DEFAULT_CONTAINER_SIZE,
  DEFAULT_FORMATTER,
  DEFAULT_TRUNCATION_MODE,
  MAX_LEGEND_HEIGHT,
  MAX_LEGEND_WIDTH,
  RANGE_LEGEND_CLASS,
} from './constants.js';
import { RangeLegendProviders } from './providers/RangeLegendProviders.js';
import { legendContainer } from './RangeLegend.sty.js';
import type { RangeLegendProps } from './types/range-legend.js';
import { getLegendScale } from './utils/get-legend-scale.js';
import { mapToInternalRangeItems } from './utils/map-to-internal-range-items.js';
import { useChartLayoutRatio } from '../../chart-layout/hooks/useChartLayoutRatio.js';
import { ORIENTATION } from '../../chart-layout/types/constants.js';
import { useLegendSize } from '../shared/context/LegendSizeContext.js';

/**
 * @internal
 */
export const RangeLegend = (props: RangeLegendProps) => {
  const {
    items,
    orientation = ORIENTATION.Horizontal,
    formatter = DEFAULT_FORMATTER,
    truncationMode = DEFAULT_TRUNCATION_MODE,
    min,
    max,
  } = props;

  const isVertical = orientation === ORIENTATION.Vertical;

  const { width, height } = useLegendSize();
  const { legendRef } = useChartLayoutRatio();

  const internalRangeItems = mapToInternalRangeItems(items);

  if (!internalRangeItems.length) {
    return null;
  }

  const legendHeight = Math.max(height, DEFAULT_CONTAINER_SIZE);
  const legendWidth = Math.max(width, DEFAULT_CONTAINER_SIZE);

  const containerLegendHeight = legendHeight - CONTAINER_PADDING;
  const containerLegendWidth = legendWidth - CONTAINER_PADDING;

  const containerSize = isVertical
    ? Math.min(containerLegendHeight, MAX_LEGEND_HEIGHT)
    : Math.min(containerLegendWidth, MAX_LEGEND_WIDTH);

  const scale = getLegendScale(
    internalRangeItems,
    containerSize,
    isVertical,
    min,
    max,
  );

  return (
    <RangeLegendProviders
      formatter={formatter}
      orientation={orientation}
      scale={scale}
      truncationMode={truncationMode}
    >
      <div
        ref={legendRef}
        className={clsx(legendContainer({ isVertical }), RANGE_LEGEND_CLASS)}
        style={{
          width: `${legendWidth}px`,
          height: `${legendHeight}px`,
        }}
      >
        <Flex
          width={`${containerLegendWidth}px`}
          height={`${containerLegendHeight}px`}
          maxWidth={MAX_LEGEND_WIDTH}
          maxHeight={MAX_LEGEND_HEIGHT}
          style={{ position: 'relative' }}
        >
          <RangeLegendSegments
            items={internalRangeItems}
            maxValue={max}
            minValue={min}
          />
          <RangeLegendMarkers
            items={internalRangeItems}
            maxValue={max}
            minValue={min}
          />
        </Flex>
      </div>
    </RangeLegendProviders>
  );
};
